import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";

import InventoryAdjustmentQueryService from "../../services/inventory/adjustment/InventoryAdjustmentQueryService";

import {
  setAdjustmentsList,
  clearAdjustmentsList,
  setSelectedAdjustment,
  setIsLoading,
  setError,
  openAdjustmentModal,
  closeAdjustmentModal,
  setPendingAdjustments,
  clearPendingAdjustments,
  setPendingAdjustmentsCount,
  openPendingAdjustmentModal,
  closePendingAdjustmentModal,
  openAdjustmentDetailModal,
  closeAdjustmentDetailModal,
} from "../../store/slices/inventory/inventoryAdjustmentQuerySlice";

import { InventoryAdjustmentFilter } from "../../types/inventory/adjustment/InventoryAdjustmentTypes";

import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";
import useModals from "../useModals";
import { useInventoryAdjustment } from "./useInventoryAdjustment";

export const useInventoryAdjustmentQuery = () => {
  const dispatch: AppDispatch = useDispatch();

  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const { setForm } = useInventoryAdjustment();

  const {
    list,
    paginator,
    selectedAdjustment,
    showAdjustmentModal,
    showPendingAdjustmentModal,
    showAdjustmentDetailModal,
    isLoading,
    error,

    // 🔹 NUEVOS
    pendingList,
    pendingCount,
  } = useSelector((state: RootState) => state.inventoryAdjustmentQuery);

  // ---------------------------------------------------------
  // 🔎 Obtener ajustes filtrados (paginados)
  // ---------------------------------------------------------
  const handlerGetFilteredAdjustments = async (
    filter: InventoryAdjustmentFilter,
    page = 0,
    size = 10,
    sort = "createdAt,desc"
  ) => {
    try {
      dispatch(setIsLoading(true));
      showLoadingIndicator();

      const response = await InventoryAdjustmentQueryService.getFiltered(
        filter,
        page,
        size,
        sort
      );

      console.log(response);

      if (response.statusCode === 200) {
        dispatch(
          setAdjustmentsList({
            content: response.data.content,
            paginator: response.data,
          })
        );
      }
    } catch (error: any) {
      dispatch(setError(error?.message || "Error al obtener ajustes."));
      handle(error);
      showAlert("error", "Error", "No se pudieron obtener los ajustes.");
    } finally {
      dispatch(setIsLoading(false));
      hideLoadingIndicator();
    }
  };

  // ---------------------------------------------------------
  // 📄 Obtener ajuste por ID (tabla)
  // ---------------------------------------------------------
  const handlerGetAdjustmentById = async (id: number) => {
    try {
      dispatch(setIsLoading(true));
      showLoadingIndicator();

      const response = await InventoryAdjustmentQueryService.getById(id);
      console.log(response);

      // const {
      //   // type,
      //   // title,
      //   // message,
      //   data,
      //   statusCode,
      // } = response;

      // if (statusCode === 200) {
      //   dispatch(setSelectedAdjustment(data));
      //   return data;
      // } else {
      //   showAlert(
      //     "error",
      //     "Error",
      //     "No se pudo cargar el ajuste."
      //   );
      // }
    } catch (error: any) {
      handle(error);
      showAlert("error", "Error", "No se pudo cargar el ajuste.");
    } finally {
      dispatch(setIsLoading(false));
      hideLoadingIndicator();
    }
  };


  // ---------------------------------------------------------
  // 🔁 Obtener formulario para continuar ajuste pendiente
  // ---------------------------------------------------------
  const handlerGetPendingAdjustmentForm = async (id: number) => {
    try {
      dispatch(setIsLoading(true));
      showLoadingIndicator();

      const response =
        await InventoryAdjustmentQueryService.getPendingForm(id);
      console.log(response);

      const {
        // type,
        // title,
        // message,
        data,
        statusCode,
      } = response;

      if (statusCode === 200) {
        dispatch(closePendingAdjustmentModal());
        dispatch(openAdjustmentModal());

        setForm(data);
      } else {
        showAlert(
          "error",
          "Error",
          "No se pudo cargar el ajuste pendiente."
        );
      }
    } catch (error: any) {
      handle(error);
      showAlert(
        "error",
        "Error",
        "No se pudo cargar el ajuste pendiente."
      );
    } finally {
      dispatch(setIsLoading(false));
      hideLoadingIndicator();
    }
  };



  // ---------------------------------------------------------
  // ⏳ Obtener ajustes pendientes
  // ---------------------------------------------------------
  const handlerGetPendingAdjustments = async () => {
    try {
      dispatch(setIsLoading(true));
      showLoadingIndicator();
      const response = await InventoryAdjustmentQueryService.getPending();
      // console.log(response);

      const {
        // type,
        // title,
        // message,
        data,
        statusCode
      } = response;

      if (statusCode === 200) {
        dispatch(setPendingAdjustments(data));
        // openModal(type, { title, message, });
      } else {
        showAlert(
          "error",
          "Error",
          "No se pudieron obtener los ajustes pendientes."
        );
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setIsLoading(false));
      hideLoadingIndicator();
    }
  };

  // ---------------------------------------------------------
  // 🔢 Obtener contador de ajustes pendientes
  // ---------------------------------------------------------
  const handlerGetPendingAdjustmentsCount = async () => {
    try {
      const response =
        await InventoryAdjustmentQueryService.getPendingCount();
      // console.log(response);

      const {
        // type,
        title,
        message,
        data,
        statusCode
      } = response;

      if (statusCode === 200) {
        dispatch(setPendingAdjustmentsCount(data));
      } else {
        showAlert(
          "error",
          title || "Error",
          message || "No se pudo obtener el contador de ajustes pendientes."
        );
      }
    } catch (error: any) {
      handle(error);
    }
  };


  // ---------------------------------------------------------
  // 🧹 Limpiezas
  // ---------------------------------------------------------
  const handlerClearAdjustments = () => {
    dispatch(clearAdjustmentsList());
  };

  const handlerClearPending = () => {
    dispatch(clearPendingAdjustments());
  };

  const handlerClearSelected = () => {
    dispatch(setSelectedAdjustment(null));
  };

  // ---------------------------------------------------------
  // 🟦 Abrir / cerrar modal
  // ---------------------------------------------------------
  const handlerOpenAdjustmentModal = () => {
    dispatch(openAdjustmentModal());
  };

  const handlerCloseAdjustmentModal = () => {
    dispatch(closeAdjustmentModal());
  };

  // ---------------------------------------------------------
  // 🟨 Abrir / cerrar modal de ajustes pendientes
  // ---------------------------------------------------------
  const handlerOpenPendingAdjustmentModal = () => {
    dispatch(openPendingAdjustmentModal());
  };

  const handlerClosePendingAdjustmentModal = () => {
    dispatch(closePendingAdjustmentModal());
  };

  // ---------------------------------------------------------
  // 🟪 Abrir / cerrar modal de detalle
  // ---------------------------------------------------------
  const handlerOpenAdjustmentDetailModal = () => {
    dispatch(openAdjustmentDetailModal());
  };

  const handlerCloseAdjustmentDetailModal = () => {
    dispatch(closeAdjustmentDetailModal());
  };



  // ---------------------------------------------------------
  // RETURN
  // ---------------------------------------------------------
  return {
    // estados
    list,
    paginator,
    selectedAdjustment,
    showAdjustmentModal,
    showPendingAdjustmentModal,
    showAdjustmentDetailModal,
    isLoading,
    error,

    pendingList,
    pendingCount,

    // handlers tabla
    handlerGetFilteredAdjustments,
    handlerGetAdjustmentById,
    handlerGetPendingAdjustmentForm,

    // handlers pendientes
    handlerGetPendingAdjustments,
    handlerGetPendingAdjustmentsCount,
    handlerClearPending,

    // UI
    handlerClearAdjustments,
    handlerClearSelected,
    handlerOpenAdjustmentModal,
    handlerCloseAdjustmentModal,
    handlerOpenPendingAdjustmentModal,
    handlerClosePendingAdjustmentModal,
    handlerOpenAdjustmentDetailModal,
    handlerCloseAdjustmentDetailModal,
  };
};
